import React, { useState, useEffect } from "react";
import { useAuth } from "@/App";
import api from "@/lib/api";
import {
  UtensilsCrossed, Calendar, Plus, Trash2, Save, Loader2, Check
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const MEALS = [
  { key: "Desayuno", placeholder: "Ej: Café con leche, tostadas con mermelada" },
  { key: "Almuerzo", placeholder: "Ej: Pollo al horno con puré de calabaza" },
  { key: "Merienda", placeholder: "Ej: Té con galletitas de avena" },
  { key: "Cena", placeholder: "Ej: Sopa crema de verduras, flan casero" },
];

export default function PortalConfigPage() {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [menu, setMenu] = useState({ Desayuno: "", Almuerzo: "", Merienda: "", Cena: "" });
  const [activities, setActivities] = useState([]);
  const [newActivity, setNewActivity] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setSaved(false);
      try {
        const res = await api.get("/family-portal/data", { params: { date: selectedDate } });
        const data = res.data;
        setMenu(data.daily_menu || { Desayuno: "", Almuerzo: "", Merienda: "", Cena: "" });
        setActivities(data.activities || []);
      } catch {
        setMenu({ Desayuno: "", Almuerzo: "", Merienda: "", Cena: "" });
        setActivities([]);
      }
      setLoading(false);
    };
    load();
  }, [selectedDate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanMenu = {};
      Object.entries(menu).forEach(([k, v]) => { if (v.trim()) cleanMenu[k] = v.trim(); });
      await api.post("/family-portal/data", {
        date: selectedDate,
        daily_menu: Object.keys(cleanMenu).length > 0 ? cleanMenu : null,
        activities: activities.filter(a => a.trim()),
      });
      toast.success("Datos del portal familiar actualizados");
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
    setSaving(false);
  };

  const addActivity = () => {
    if (newActivity.trim()) {
      setActivities([...activities, newActivity.trim()]);
      setNewActivity("");
    }
  };

  const removeActivity = (i) => {
    setActivities(activities.filter((_, idx) => idx !== i));
  };

  const isToday = selectedDate === new Date().toISOString().split("T")[0];
  const dayLabel = new Date(selectedDate + "T12:00:00").toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="portal-config-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-[Manrope]">Portal Familiar</h1>
          <p className="text-sm text-slate-400 mt-0.5">Cargá el menú del día y actividades que verán los familiares</p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            className="rounded-xl h-10 bg-slate-50 border-slate-200 w-44"
            data-testid="portal-date-picker"
          />
          {isToday && <Badge className="rounded-full bg-violet-100 text-violet-700 border-0 text-[10px] font-bold">HOY</Badge>}
        </div>
      </div>

      <p className="text-sm text-slate-500 capitalize">{dayLabel}</p>

      {loading ? (
        <p className="text-slate-400 text-center py-12">Cargando...</p>
      ) : (
        <>
          {/* Menu del Día */}
          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                  <UtensilsCrossed className="w-5 h-5 text-amber-600" strokeWidth={1.8} />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-900 text-sm">Menú del Día</h2>
                  <p className="text-xs text-slate-400">Se muestra a los familiares en su portal</p>
                </div>
              </div>
              <div className="space-y-4">
                {MEALS.map(meal => (
                  <div key={meal.key} className="space-y-1.5">
                    <Label className="text-slate-700 font-medium text-sm">{meal.key}</Label>
                    <Input
                      value={menu[meal.key] || ""}
                      onChange={e => setMenu({ ...menu, [meal.key]: e.target.value })}
                      placeholder={meal.placeholder}
                      className="rounded-xl h-10 bg-slate-50 border-slate-200"
                      data-testid={`menu-${meal.key.toLowerCase()}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Actividades */}
          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-emerald-600" strokeWidth={1.8} />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-900 text-sm">Actividades del Día</h2>
                  <p className="text-xs text-slate-400">Agregá las actividades programadas</p>
                </div>
              </div>

              {activities.length > 0 && (
                <div className="space-y-2 mb-4">
                  {activities.map((act, i) => (
                    <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3" data-testid={`activity-item-${i}`}>
                      <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                      </div>
                      <p className="text-sm text-slate-700 flex-1">{act}</p>
                      <button
                        onClick={() => removeActivity(i)}
                        className="text-slate-300 hover:text-rose-500 transition-colors"
                        data-testid={`remove-activity-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  value={newActivity}
                  onChange={e => setNewActivity(e.target.value)}
                  placeholder="Ej: Taller de pintura 10:00hs"
                  className="rounded-xl h-10 bg-slate-50 border-slate-200 flex-1"
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addActivity(); } }}
                  data-testid="new-activity-input"
                />
                <Button
                  onClick={addActivity}
                  disabled={!newActivity.trim()}
                  className="rounded-xl h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="add-activity-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Save */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="rounded-xl h-12 px-8 bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm active:scale-[0.98] transition-all w-full sm:w-auto"
            data-testid="save-portal-data-btn"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
            ) : saved ? (
              <><Check className="w-4 h-4 mr-2" /> Guardado</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Guardar cambios</>
            )}
          </Button>
        </>
      )}
    </div>
  );
}
